/**
 * Marketing attribution capture (Phase 2).
 *
 * Captures *only* non-sensitive marketing / referrer / page metadata so the
 * owner signup-notification email can show how a teacher found DoneGrading.
 *
 * Privacy rules (hard constraints):
 * - NO student data (names, emails, grades, assignments, classroom content).
 * - NO exact location. NO cookies. localStorage only.
 * - Page fields store the URL *path only* (no query string / no hash) so we
 *   never accidentally persist OAuth codes, tokens, or other query secrets.
 * - Referrer is reduced to origin + path (query/hash stripped).
 *
 * First-touch model: the very first visit's UTM/referrer/landing values are
 * preserved forever. `last_seen_at` always advances; `signup_page` is set when
 * the user reaches the auth screen.
 */

export const ATTRIBUTION_STORAGE_KEY = 'dg_attribution_v1';

/** Stored shape (snake_case to match the backend payload contract). */
export interface StoredAttribution {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
  landing_page: string | null;
  signup_page: string | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
}

/** First-touch fields: immutable once captured. */
const FIRST_TOUCH_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'referrer',
  'landing_page',
] as const;

const UTM_PARAMS: Array<[keyof StoredAttribution, string]> = [
  ['utm_source', 'utm_source'],
  ['utm_medium', 'utm_medium'],
  ['utm_campaign', 'utm_campaign'],
  ['utm_content', 'utm_content'],
  ['utm_term', 'utm_term'],
];

const MAX_VALUE_LENGTH = 256;

function emptyAttribution(): StoredAttribution {
  return {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_content: null,
    utm_term: null,
    referrer: null,
    landing_page: null,
    signup_page: null,
    first_seen_at: null,
    last_seen_at: null,
  };
}

/** Trim, collapse to null when empty, and cap length. */
export function sanitizeValue(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, MAX_VALUE_LENGTH);
}

/** Reduce a referrer URL to `origin + pathname` (drop query + hash). */
export function cleanReferrer(raw: unknown): string | null {
  const value = sanitizeValue(raw);
  if (!value) return null;
  try {
    const url = new URL(value);
    return `${url.origin}${url.pathname}`.replace(/\/$/, '') || url.origin;
  } catch {
    // Not a parseable absolute URL — store the trimmed value as-is.
    return value;
  }
}

/**
 * Pure: derive the "incoming" attribution from URL/referrer/path inputs.
 * `search` is a raw query string (e.g. `location.search`); `path` is the
 * pathname only.
 */
export function buildIncomingAttribution(input: {
  search?: string;
  referrer?: string;
  path?: string;
  now: string;
}): Partial<StoredAttribution> {
  const params = new URLSearchParams(input.search ?? '');
  const incoming: Partial<StoredAttribution> = {};
  for (const [key, param] of UTM_PARAMS) {
    const value = sanitizeValue(params.get(param));
    if (value) incoming[key] = value;
  }
  const referrer = cleanReferrer(input.referrer);
  if (referrer) incoming.referrer = referrer;
  const landing = sanitizeValue(input.path);
  if (landing) incoming.landing_page = landing;
  incoming.first_seen_at = input.now;
  incoming.last_seen_at = input.now;
  return incoming;
}

/**
 * Pure: merge a freshly-observed `incoming` snapshot into the stored value,
 * preserving first-touch fields and advancing `last_seen_at`.
 */
export function mergeAttribution(
  existing: StoredAttribution | null,
  incoming: Partial<StoredAttribution>,
  now: string
): StoredAttribution {
  const base = existing ? { ...existing } : emptyAttribution();

  for (const key of FIRST_TOUCH_KEYS) {
    // First-touch is immutable: only fill in if we have never captured it.
    if (!base[key] && incoming[key]) base[key] = incoming[key] as string;
  }

  base.first_seen_at = base.first_seen_at ?? incoming.first_seen_at ?? now;
  base.last_seen_at = now;
  return base;
}

/** True when at least one non-timestamp attribution field is populated. */
export function hasAnyAttribution(value: StoredAttribution | null): boolean {
  if (!value) return false;
  return (
    !!value.utm_source ||
    !!value.utm_medium ||
    !!value.utm_campaign ||
    !!value.utm_content ||
    !!value.utm_term ||
    !!value.referrer ||
    !!value.landing_page ||
    !!value.signup_page
  );
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

/**
 * Pure: derive a meaningful app route from a location, preserving the hash
 * route used by HashRouter (e.g. `/#/grade`) instead of collapsing to `/`.
 * Drops any nested query/hash so OAuth fragments (#access_token=…) are never
 * persisted; anchors and token-like fragments fall back to the pathname.
 */
export function routeFromLocation(loc: { pathname?: string; hash?: string }): string {
  const path = sanitizeValue(loc.pathname) ?? '/';
  const hash = typeof loc.hash === 'string' ? loc.hash : '';
  if (hash.startsWith('#')) {
    const cleaned = hash.slice(1).split('?')[0].split('#')[0];
    if (cleaned.startsWith('/') && cleaned !== '/' && !cleaned.includes('=')) {
      return `${path}#${cleaned}`;
    }
  }
  return path;
}

function currentRoute(): string {
  if (typeof window === 'undefined') return '/';
  return routeFromLocation(window.location);
}

export function readStoredAttribution(): StoredAttribution | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;
    const result = emptyAttribution();
    for (const key of Object.keys(result) as Array<keyof StoredAttribution>) {
      result[key] = sanitizeValue(parsed[key]);
    }
    return result;
  } catch {
    return null;
  }
}

function writeStoredAttribution(value: StoredAttribution): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage disabled / full — attribution is best-effort, never throw.
  }
}

/**
 * Capture attribution on app load. Reads URL/referrer/path from `window`,
 * merges into the stored first-touch value, persists, and returns the result.
 * Safe to call on every load; first-touch fields are preserved.
 */
export function captureAttribution(
  now: string = new Date().toISOString()
): StoredAttribution | null {
  if (!isBrowser()) return null;
  const incoming = buildIncomingAttribution({
    search: window.location.search,
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    path: currentRoute(),
    now,
  });
  const merged = mergeAttribution(readStoredAttribution(), incoming, now);
  writeStoredAttribution(merged);
  return merged;
}

/**
 * Record that the user reached the auth/signup screen. Updates `signup_page`
 * and `last_seen_at` without disturbing first-touch fields.
 */
export function markSignupPage(
  page?: string,
  now: string = new Date().toISOString()
): StoredAttribution | null {
  if (!isBrowser()) return null;
  const existing = readStoredAttribution() ?? captureAttribution(now);
  const base = existing ?? emptyAttribution();
  const signupPage = sanitizeValue(page) ?? currentRoute();
  base.signup_page = signupPage;
  base.last_seen_at = now;
  if (!base.first_seen_at) base.first_seen_at = now;
  writeStoredAttribution(base);
  return base;
}

/** The payload to send to the backend on a successful sign-in. */
export function getSignupAttributionPayload(): StoredAttribution | null {
  const stored = readStoredAttribution();
  return hasAnyAttribution(stored) || stored?.first_seen_at ? stored : null;
}
